import type { RecordingId } from "@repo/app-types/entities/recording";
import type { FC, ReactNode } from "react";
import type {
  QuoteMatchMap,
  QuoteRecordingMatch,
} from "../gradeValidationHelpers.js";
import { QuoteMatchMap as QuoteMatchMapUtil } from "../gradeValidationHelpers.js";
import type { FocusedSnippetState } from "./useTranscriptSnippetSharing.js";

export interface RecordingFragmentTextProps {
  text: string;
  recordingId: RecordingId;
  fragmentIndex: number;
  activeSnippet?: FocusedSnippetState;
  quoteMatches?: QuoteMatchMap;
  searchMatches?: QuoteMatchMap;
}

type HighlightVariant = "citation" | "search";

const VARIANT_CLASS: Record<HighlightVariant, string> = {
  citation: "border-b border-blue-500 bg-blue-100 px-1 py-px text-blue-800",
  // Matches the search-results blurb highlight (gradingsTableContactCell).
  search: "rounded bg-amber-200/70 px-0.5 text-amber-900",
};

interface HighlightRange {
  start: number;
  end: number;
  variant: HighlightVariant;
  /** DOM id of the rendered span — the anchor `focusQuote` scrolls/flashes. */
  id?: string;
}

/**
 * Render `text` with each range wrapped in a styled span. Ranges may overlap;
 * a range is clipped to start where the previous one ended (and dropped when
 * fully covered).
 */
function renderHighlightedText(
  text: string,
  ranges: readonly HighlightRange[],
): ReactNode {
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const [index, range] of ranges
    .toSorted((a, b) => a.start - b.start)
    .entries()) {
    const start = Math.max(range.start, cursor);
    if (range.end <= start) {
      continue;
    }
    if (start > cursor) {
      parts.push(text.slice(cursor, start));
    }
    parts.push(
      <span key={index} id={range.id} className={VARIANT_CLASS[range.variant]}>
        {text.slice(start, range.end)}
      </span>,
    );
    cursor = range.end;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return parts;
}

function toHighlightRanges(
  matches: readonly QuoteRecordingMatch[],
  variant: HighlightVariant,
): HighlightRange[] {
  return matches.map((match) => ({
    start: match.inFragmentIndex,
    end: match.inFragmentIndex + match.length,
    variant,
    id: `quote-${match.quoteId}-${match.quoteIndex}`,
  }));
}

/**
 * Renders the text of a recording fragment, highlighting the active shared
 * snippet (yellow), the current transcript-search match (amber), and copilot
 * citation quotes (blue) — in that precedence order per fragment.
 */
export const RecordingFragmentText: FC<RecordingFragmentTextProps> = ({
  text,
  recordingId,
  fragmentIndex,
  activeSnippet,
  quoteMatches,
  searchMatches,
}) => {
  // Check if this fragment is in an active snippet
  const isInActiveSnippet =
    !!activeSnippet &&
    activeSnippet.recordingId === recordingId &&
    fragmentIndex >= activeSnippet.startFragmentIndex &&
    fragmentIndex <= activeSnippet.endFragmentIndex;

  // Render yellow snippet highlighting
  if (isInActiveSnippet) {
    const isSnippetStart =
      activeSnippet.recordingId === recordingId &&
      fragmentIndex === activeSnippet.startFragmentIndex;
    const isSnippetEnd =
      activeSnippet.recordingId === recordingId &&
      fragmentIndex === activeSnippet.endFragmentIndex;

    const startOffset = isSnippetStart
      ? activeSnippet.startCharOffset
      : undefined;
    const endOffset = isSnippetEnd ? activeSnippet.endCharOffset : undefined;

    const snippetStart =
      isSnippetStart && typeof startOffset === "number" ? startOffset : 0;
    const snippetEnd =
      isSnippetEnd && typeof endOffset === "number" ? endOffset : text.length;

    const validatedStart = Math.max(0, Math.min(snippetStart, text.length));
    const validatedEnd = Math.max(
      validatedStart,
      Math.min(snippetEnd, text.length),
    );

    const before = text.slice(0, validatedStart);
    const highlighted = text.slice(validatedStart, validatedEnd);
    const after = text.slice(validatedEnd);

    return (
      <span>
        {before && <span>{before}</span>}
        {validatedStart !== validatedEnd && (
          <span className="rounded-[4px] border border-yellow-200 bg-yellow-100 px-0.5 text-yellow-900">
            {highlighted}
          </span>
        )}
        {after && <span>{after}</span>}
      </span>
    );
  }

  // Render amber highlighting for the current transcript-search match
  const searchHits = searchMatches
    ? QuoteMatchMapUtil.findByRecording(
        searchMatches,
        recordingId,
        fragmentIndex,
      )
    : undefined;
  if (searchHits && searchHits.length > 0) {
    return (
      <span>
        {renderHighlightedText(text, toHighlightRanges(searchHits, "search"))}
      </span>
    );
  }

  // Render blue quote highlighting
  const citationHits = quoteMatches
    ? QuoteMatchMapUtil.findByRecording(
        quoteMatches,
        recordingId,
        fragmentIndex,
      )
    : undefined;
  if (!citationHits || citationHits.length === 0) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {renderHighlightedText(text, toHighlightRanges(citationHits, "citation"))}
    </span>
  );
};
